/**
 * <cart-upsell-carousel> — prev/next arrows for the scroll-snap track in the cart drawer.
 * Scrolling itself is native CSS scroll-snap, so swiping works without JS; the arrows are
 * progressive enhancement. Re-queries refs on every click because the drawer's DOM is morphed
 * on each cart update.
 */
class CartUpsellCarousel extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    // A fresh controller per connect: the morph can detach and re-attach this element,
    // and an aborted controller can't be reused.
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    const track = this.#track;
    track?.addEventListener('scroll', this.#updateArrows, { signal, passive: true });

    // The track is 0px wide while the drawer dialog is closed, so arrow state is
    // recomputed whenever it gets its real size.
    if (track && typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(this.#updateArrows);
      this.#resizeObserver.observe(track);
    }
    this.#updateArrows();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[ref="track"]'));
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('[ref="prev"], [ref="next"]');
    if (!button) return;
    const track = this.#track;
    if (!track) return;
    const direction = button.getAttribute('ref') === 'next' ? 1 : -1;
    track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
  };

  #updateArrows = () => {
    const track = this.#track;
    if (!track) return;
    const prev = this.querySelector('[ref="prev"]');
    const next = this.querySelector('[ref="next"]');
    const atStart = track.scrollLeft <= 2;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    if (prev instanceof HTMLButtonElement) prev.disabled = atStart;
    if (next instanceof HTMLButtonElement) next.disabled = atEnd;
  };
}

if (!customElements.get('cart-upsell-carousel')) {
  customElements.define('cart-upsell-carousel', CartUpsellCarousel);
}
